import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import Spinner from "aldoo-ra/Spinner"
import UserAccessWidget from "../UserAccessWidget"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Localization from "aldoo-ra/Localization"
import { showMessageDialog } from "aldoo-ra/MessageDialog"
import { FeatureLockAPI } from "aldoo-ra/FeatureLock/api"
import {
  UserPlusIcon,
  XMarkIcon,
  EnvelopeIcon,
  UserGroupIcon,
  BookOpenIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  CheckIcon,
  PlusIcon,
} from "@heroicons/react/24/outline"
import { listContacts, listGroups } from "../Contacts/controller"
import { listOrgs, listMembers } from "aldoo-ra/Organizations/api"
import { SendShareLink } from "./api"
import GoPremiumDialog, { openGoPremium } from "../GoPremiumDialog"
import { showToast, TOAST_POSITIONS } from "aldoo-ra/Toast"

const RECIPIENTS_FEATURE = "max-share-recipients"
const DEFAULT_MAX = 10
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const normalize = (e) => String(e || "").trim().toLowerCase()

// ── Add Recipient dialog ──────────────────────────────────────────────────────
function AddRecipientDialog({ open, onClose, onAdd, people, groups, teams, remaining, existing, texts }) {
  const [query, setQuery] = useState("")

  const q = query.trim().toLowerCase()
  const suggestions = useMemo(() => {
    if (!q) return []
    return people
      .filter((p) => !existing.has(p.email) && ((p.name || "").toLowerCase().includes(q) || p.email.toLowerCase().includes(q)))
      .slice(0, 8)
  }, [people, q, existing])

  // Groups + teams flow through the same suggestion list — typing a name
  // (or part of it) surfaces the group/team chip and clicking it adds all
  // of its emails at once.
  const groupSuggestions = useMemo(() => {
    if (!q) return []
    const combined = [
      ...(groups || []).map((g) => ({ ...g, _kind: "group" })),
      ...(teams || []).map((t) => ({ ...t, _kind: "team" })),
    ]
    return combined
      .filter((g) => g.emails && g.emails.length > 0 && (g.name || "").toLowerCase().includes(q))
      .slice(0, 6)
  }, [groups, teams, q])

  const queryIsEmail = EMAIL_RE.test(query.trim()) && !existing.has(query.trim().toLowerCase())

  const add = (emails) => {
    onAdd(emails)
    setQuery("")
  }

  return (
    <Modal 
      variant="transfer_compact"
      isOpen={open} 
      onClose={onClose} 
      title={texts?.addRecipients}
      className="max-w-md" 
      closeOnClickOutside 
      closeOnEscape
    >
      <div className="p-5 flex flex-col gap-4">
        <div className="text-xs text-text_secondary">
          {remaining} {texts?.remaining}
        </div>

        {/* Free-form + autosuggest */}
        <div>
          <div className="relative">
            <Input
              variant="transfer_compact"
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && queryIsEmail) add([query.trim()]) }}
              icon={<MagnifyingGlassIcon className="w-4 h-4 text-text_secondary stroke-2 pointer-events-none" />}
              placeholder={texts?.emailOrSearch}
              className="w-full"
              showClearButton
              onClear={() => setQuery("")}
            />
          </div>

          {(suggestions.length > 0 || groupSuggestions.length > 0 || queryIsEmail) && (
            <div className="mt-1 rounded-xl border border-bordered overflow-hidden divide-y divide-bordered max-h-56 overflow-y-auto">
              {queryIsEmail && (
                <button
                  onClick={() => add([query.trim()])}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-black/5"
                >
                  <EnvelopeIcon className="w-4 h-4 text-primary shrink-0" />
                  <span className="text-sm text-text truncate">
                    {texts?.add} "{query.trim()}"
                  </span>
                </button>
              )}

              {groupSuggestions.map((g) => (
                <button
                  key={`${g._kind}-${g.id}`}
                  onClick={() => add(g.emails)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-black/5"
                >
                  <UserGroupIcon className="w-4 h-4 text-text_secondary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-text truncate">{g.name}</div>
                    <div className="text-xs text-text_secondary truncate">
                      {g._kind === "team" ? texts?.team : texts?.group} · {g.emails.length}
                    </div>
                  </div>
                  <PlusIcon className="w-4 h-4 text-primary shrink-0" />
                </button>
              ))}

              {suggestions.map((p) => (
                <button key={p.email} onClick={() => add([p.email])} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-black/5">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-text truncate">{p.name || p.email}</div>
                    {p.name && <div className="text-xs text-text_secondary truncate">{p.email}</div>}
                  </div>
                  <PlusIcon className="w-4 h-4 text-primary shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>

        <Button variant="primary" className="w-full" onClick={onClose}>
          {texts?.done}
        </Button>
      </div>
    </Modal>
  )
}

// ── Link sender panel ──────────────────────────────────────────────────────────
// `sendFn` (optional): override the default outbound-share send path so the
// same component can drive any "email these N people about this link" flow
// (e.g. receive-link invites). Signature: async (emails, message) => res.
// Defaults to ShareAPI.SendShareLink against `orderID`.
export default function LinkSender({ orderID, accessType, alreadySent = 0, onSent, embedded = false, sendFn }) {
  const texts = TextBundle({ id: "transfer_share" })
  const { translateWithLanguage, getLanguage } = Localization()
  const t = (key, data) => translateWithLanguage(key, getLanguage(), data)

  const [recipients, setRecipients] = useState([])
  const [message, setMessage] = useState("")
  const [messageOpen, setMessageOpen] = useState(true)
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  // Cumulative recipients already sent for this share (seeded from the prop,
  // updated after each send) so "Send again" budgets against the running total.
  const [totalSent, setTotalSent] = useState(alreadySent)
  const [lastSentCount, setLastSentCount] = useState(0)
  const [showDialog, setShowDialog] = useState(false)
  const [max, setMax] = useState(DEFAULT_MAX)
  const [contacts, setContacts] = useState([])
  const [groups, setGroups] = useState([])
  const [teams, setTeams] = useState([])

  useEffect(() => {
    FeatureLockAPI.getFeatureParam(RECIPIENTS_FEATURE, "value")
      .then((v) => { const n = parseInt(v, 10); if (Number.isFinite(n) && n > 0) setMax(n) })
      .catch(() => {})

    listContacts().then((r) => { if (r?.result) setContacts(r.contacts || []) }).catch(() => {})
    listGroups().then((r) => { if (r?.result) setGroups(r.groups || []) }).catch(() => {})
    listOrgs().then(async (r) => {
      const orgs = r?.orgs || []
      const resolved = await Promise.all(orgs.map(async (o) => {
        const oid = o.id || o._id
        const m = await listMembers(oid).catch(() => null)
        const emails = (m?.members || []).map((x) => normalize(x.email)).filter((e) => EMAIL_RE.test(e))
        return { id: oid, name: o.name, emails: [...new Set(emails)] }
      }))
      setTeams(resolved)
    }).catch(() => {})
  }, [])

  const contactById = useMemo(() => {
    const map = new Map()
    for (const c of contacts) map.set(c.userId, c)
    return map
  }, [contacts])

  const people = useMemo(() => {
    const seen = new Set()
    const out = []
    const push = (name, email) => {
      const e = normalize(email)
      if (!EMAIL_RE.test(e) || seen.has(e)) return
      seen.add(e); out.push({ name, email: e })
    }
    for (const c of contacts) push(c.name, c.email)
    for (const tm of teams) for (const e of tm.emails) push("", e)
    return out
  }, [contacts, teams])

  const resolvedGroups = useMemo(() =>
    groups.map((g) => ({
      id: g.groupId,
      name: g.name,
      emails: [...new Set((g.members || []).map((uid) => normalize(contactById.get(uid)?.email)).filter((e) => EMAIL_RE.test(e)))],
    })), [groups, contactById])

  const budget = Math.max(0, max - totalSent)

  // Track recipients via ref so successive synchronous calls (group picker
  // expanding into many emails) see the latest set without waiting for
  // state to flush.
  const recipientsRef = useRef(recipients)
  useEffect(() => { recipientsRef.current = recipients }, [recipients])

  // Pending-add buffer + debounced toast so rapid sequential calls coalesce
  // into one "N people added" toast instead of one per email.
  const pendingToastRef = useRef([])
  const toastTimerRef = useRef(null)

  const addEmails = useCallback((emails) => {
    const set = new Set(recipientsRef.current)
    const added = []
    for (const e of emails) {
      const n = normalize(e)
      if (EMAIL_RE.test(n) && !set.has(n) && set.size < budget) {
        set.add(n)
        added.push(n)
      }
    }
    if (added.length === 0) return
    const next = [...set]
    recipientsRef.current = next
    setRecipients(next)

    pendingToastRef.current.push(...added)
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    toastTimerRef.current = setTimeout(() => {
      const batch = pendingToastRef.current
      pendingToastRef.current = []
      toastTimerRef.current = null
      const toastOpts = {
        position: TOAST_POSITIONS.TOP_CENTER, variant: "success", duration: 3000,
        enableDismissalTracking: false, styleOverrides: { progressContainer: "hidden" },
      }
      if (batch.length === 1) {
        showToast({ ...toastOpts, message: `${batch[0]} ${texts?.added}` })
      } else {
        showToast({
          ...toastOpts,
          message: t(texts?.recipientsAddedFmt || "{{count}} recipients added", { count: batch.length }),
        })
      }
    }, 80)
  }, [budget, texts])

  const remaining = Math.max(0, budget - recipients.length)

  const handleSend = async () => {
    if (recipients.length === 0) {
      showToast({ position: TOAST_POSITIONS.TOP_CENTER, variant: "error", duration: 3000, enableDismissalTracking: false, styleOverrides: { progressContainer: "hidden" }, message: texts?.pleaseEnterRecipients })
      return
    }
    setSending(true)
    try {
      const res = sendFn
        ? await sendFn(recipients, message)
        : await SendShareLink(orderID, recipients, message)
      if (res?.error === "recipients_exceeded") {
        showMessageDialog({ title: texts?.limitReached, message: `${texts?.sendAtMost} ${res.max} ${texts?.recipientsOnPlan}` })
        return
      }
      if (res?.error) throw new Error(res.error)
      const newTotal = res?.recipients_sent ?? totalSent + recipients.length
      setTotalSent(newTotal)
      setLastSentCount(recipients.length)
      setSent(true)
      onSent?.(newTotal, recipients.length)
    } catch (err) {
      showMessageDialog({ title: texts?.error, message: err.message || texts?.failedSendLink })
    } finally {
      setSending(false)
    }
  }

  const handleSendAgain = () => {
    setRecipients([])
    setMessage("")
    setSent(false)
  }

  if (sent) {
    // Remaining quota after the send just completed.
    const canSendMore = max - totalSent > 0
    return (
      <div className="w-full bg-paper rounded-2xl p-5 text-center">
        <div className="w-10 h-10 rounded-full bg-success/10 text-success flex items-center justify-center mx-auto mb-2">
          <CheckIcon className="w-6 h-6 stroke-2" />
        </div>

        <Typography variant="txt1_accent" className="block">
          {texts?.linkSentTo} {lastSentCount} {lastSentCount === 1 ? texts?.recipient : texts?.recipients}
        </Typography>

        {canSendMore && (
          <button
            onClick={handleSendAgain}
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            <PaperAirplaneIcon className="w-4 h-4" />
            {texts?.sendAgain}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className={embedded ? "w-full" : "w-full bg-paper rounded-2xl p-5"}>
      <Typography variant="h4" className="block text-text mb-3">
        {texts?.sendOverEmail}
      </Typography>

      {/* Inline contacts/groups search + chip list — same widget the
          User Access tab uses, so recipients can be added without a
          secondary dialog. */}
      <div className="mt-2 mb-4">
        <UserAccessWidget
          emails={recipients}
          onAdd={(email) => addEmails([email])}
          onRemove={(email) => setRecipients((prev) => prev.filter((e) => e !== email))}
          hideHeader
        />
      </div>

      {/* Row above the Send button: budget + upsell on the left, Add
          message toggle on the right. The note input expands below the
          row when the toggle is open. */}
      <div className="mt-3 mb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-text_secondary">
              {recipients.length} / {budget}
            </span>
            <Button variant="text_btn_primary" onClick={openGoPremium}>
              {texts?.needMore}
            </Button>
          </div>
          <button
            type="button"
            onClick={() => setMessageOpen((v) => !v)}
            className="text-sm font-medium text-text_secondary hover:text-text inline-flex items-center gap-1"
          >
            <span>{messageOpen ? texts?.hideMessage : texts?.addMessage}</span>
            <span className={`inline-block transition-transform ${messageOpen ? "rotate-180" : ""}`}>▾</span>
          </button>
        </div>
        {messageOpen && (
          <Input
            variant="transfer_compact"
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={texts?.addMessageOptional}
            className="w-full mt-3 min-h-[120px]"
          />
        )}
      </div>

      <Button 
        variant="primary" 
        className="w-full" 
        onClick={handleSend} disabled={sending}
      >
        {sending 
          ? <Spinner className="w-[16px] h-[16px]" /> 
          : <PaperAirplaneIcon className="w-4 h-4 stroke-2" />
        }
        {sending ? texts?.sending : texts?.send}
      </Button>

      <GoPremiumDialog />
    </div>
  )
}
